import React, { Component } from "react";
import PropTypes from "prop-types";
import connectAndFetch from "utils/connectAndFetch";
import Layout from "frontend/components/layout";
import Utility from "frontend/components/utility";
import CheckFrontendMode from "global/containers/CheckFrontendMode";
import ProjectCollection from "frontend/components/project-collection";
import { entityStoreActions } from "actions";
import { select, grab, meta, isEntityLoaded } from "utils/entityUtils";
import { projectCollectionsAPI, projectsAPI, requests } from "api";
import lh from "helpers/linkHandler";
import HeadContent from "global/components/HeadContent";
import omitBy from "lodash/omitBy";
import queryString from "query-string";
import debounce from "lodash/debounce";
import EventTracker, { EVENTS } from "global/components/EventTracker";
import has from "lodash/has";
import withSettings from "hoc/with-settings";

const { request, flush } = entityStoreActions;
const page = 1;
const perPage = 20;

export class ProjectCollectionDetailContainer extends Component {
  static fetchData = (getState, dispatch, location, match) => {
    const state = getState();
    const promises = [];
    const projectCollectionId = match.params.id;

    if (!isEntityLoaded("projectCollections", projectCollectionId, state)) {
      const projectCollectionRequest = request(
        projectCollectionsAPI.show(match.params.id),
        requests.feProjectCollection
      );
      const { promise } = dispatch(projectCollectionRequest);

      promises.push(promise);
    }

    const params = queryString.parse(location.search);
    const pagination = {
      number: params.page ? params.page : page,
      size: perPage
    };
    const filter = omitBy(params, (v, k) => k === "page");
    filter.collectionOrder = projectCollectionId;
    const projectsRequest = request(
      projectsAPI.index(filter, pagination),
      requests.feCollectionProjects
    );
    const { promise } = dispatch(projectsRequest);
    promises.push(promise);

    return Promise.all(promises);
  };

  static mapStateToProps = (state, ownProps) => {
    return {
      projectCollection: grab(
        "projectCollections",
        ownProps.match.params.id,
        state.entityStore
      ),
      projects: select(requests.feCollectionProjects, state.entityStore),
      projectsMeta: meta(requests.feCollectionProjects, state.entityStore),
      authentication: state.authentication
    };
  };

  static propTypes = {
    projectCollection: PropTypes.object,
    authentication: PropTypes.object,
    settings: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    location: PropTypes.object,
    match: PropTypes.object
  };

  constructor(props) {
    super(props);
    this.state = this.initialState(queryString.parse(props.location.search));
    this.updateResults = debounce(this.updateResults.bind(this), 250);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.search === this.props.location.search) return null;
    this.setState(
      this.initialState(queryString.parse(this.props.location.search)),
      this.updateResults
    );
  }

  componentWillUnmount() {
    this.props.dispatch(flush(requests.feProjectCollection));
  }

  initialState(init) {
    const filter = omitBy(init, (vIgnored, k) => k === "page");

    return {
      filter: { ...filter },
      pagination: {
        number: init.page || page,
        size: perPage
      }
    };
  }

  updateResults(filter = this.state.filter) {
    const updatedFilter = {
      ...filter,
      collectionOrder: this.props.projectCollection.id
    };

    const action = request(
      projectsAPI.index(updatedFilter, this.state.pagination),
      requests.feCollectionProjects
    );
    this.props.dispatch(action);
  }

  filterChangeHandler = filter => {
    this.setState({ filter }, () => {
      this.updateResults(filter);
    });
  };

  handlePageChange = pageParam => {
    const pagination = { ...this.state.pagination, number: pageParam };
    this.setState({ pagination }, this.updateResults);
  };

  pageChangeHandlerCreator = pageParam => {
    return event => {
      event.preventDefault();
      this.handlePageChange(pageParam);
    };
  };

  renderProjects(props) {
    if (!props.projects || !props.projectsMeta) return null;

    return (
      <ProjectCollection.Detail
        projectCollection={props.projectCollection}
        projects={props.projects}
        pagination={props.projectsMeta.pagination}
        paginationClickHandler={this.pageChangeHandlerCreator}
        authentication={props.authentication}
        filterChangeHandler={this.filterChangeHandler}
        initialState={this.state.filter || {}}
        dispatch={this.props.dispatch}
      />
    );
  }

  get description() {
    const { projectCollection } = this.props;
    if (!projectCollection) return null;
    const {
      descriptionPlaintext,
      socialDescription
    } = projectCollection.attributes;
    return socialDescription || descriptionPlaintext;
  }

  get title() {
    const { projectCollection, settings } = this.props;
    if (!projectCollection || !settings) return null;
    const { socialTitle, title } = projectCollection.attributes;
    return (
      socialTitle ||
      `\u201c${title}\u201d on ${settings.attributes.general.installationName}`
    );
  }

  get image() {
    const { projectCollection } = this.props;
    if (!projectCollection) return null;
    const { socialImageStyles, heroStyles } = projectCollection.attributes;
    if (has(socialImageStyles, "mediumLandscape"))
      return socialImageStyles.mediumLandscape;
    if (has(heroStyles, "mediumLandscape")) return heroStyles.mediumLandscape;
    return null;
  }

  render() {
    const { projectCollection } = this.props;
    if (!projectCollection) return null;

    return (
      <div>
        <CheckFrontendMode
          debugLabel="ProjectCollectionDetail"
          isProjectSubpage
        />
        {this.props.projectCollection && (
          <EventTracker
            event={EVENTS.VIEW_RESOURCE}
            resource={this.props.projectCollection}
          />
        )}
        <Utility.BackLinkPrimary
          link={lh.link("frontendProjectCollections")}
          backText={"Back to Project Collections"}
        />
        <HeadContent
          title={this.title}
          description={this.description}
          image={this.image}
        />
        <h1 className="screen-reader-text">
          {this.props.projectCollection.attributes.title}
        </h1>
        {this.renderProjects(this.props)}
        <Layout.ButtonNavigation
          showFollowing={false}
          showProjects={false}
          grayBg={false}
          showProjectCollections
          hideAtNarrow
        />
      </div>
    );
  }
}

export default connectAndFetch(withSettings(ProjectCollectionDetailContainer));
